import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { upsertProfile } from '../lib/emtRepo';
import { upsertEmployerProfile } from '../lib/employerRepo';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  applyMarketingTheme,
  applyThemePreference,
  clearMarketingTheme,
  DEFAULT_APP_THEME,
  getStoredThemePreference,
  isMarketingRoute,
  readProfileThemePreference,
  resolveTheme,
  setStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '../lib/theme';

type ThemeContextValue = {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  isMarketing: boolean;
  setThemePreference: (preference: ThemePreference) => Promise<void>;
  toggleLightDark: () => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user, role, profile } = useAuth();
  const isMarketing = isMarketingRoute(pathname);

  const [preference, setPreference] = useState<ThemePreference>(
    () => getStoredThemePreference() ?? DEFAULT_APP_THEME,
  );
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredThemePreference() ?? DEFAULT_APP_THEME),
  );

  const syncPreference = useCallback((next: ThemePreference) => {
    setPreference(next);
    setStoredThemePreference(next);
    setResolved(applyThemePreference(next));
  }, []);

  useEffect(() => {
    if (isMarketing) {
      applyMarketingTheme();
      return () => {
        clearMarketingTheme();
        setResolved(applyThemePreference(preference));
      };
    }
    clearMarketingTheme();
    setResolved(applyThemePreference(preference));
  }, [isMarketing, preference]);

  useEffect(() => {
    if (isMarketing || !user?.uid || !profile) return;
    const remote = readProfileThemePreference(profile);
    if (remote) {
      if (remote !== preference) syncPreference(remote);
      return;
    }
    if (preference !== DEFAULT_APP_THEME) {
      syncPreference(DEFAULT_APP_THEME);
    }
  }, [isMarketing, user?.uid, profile?.preferences?.theme, preference, syncPreference]);

  useEffect(() => {
    if (isMarketing || preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(applyThemePreference('system'));
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [isMarketing, preference]);

  const setThemePreference = useCallback(
    async (next: ThemePreference) => {
      if (isMarketing) return;
      syncPreference(next);

      if (!user?.uid) return;

      // Institute accounts: write ONLY the theme. upsertProfile stamps role: "emt", which the profile
      // rules reject for any non-EMT (role may never change) — the save failed and the profile effect
      // above then snapped an institute back to light on every page.
      if (profile?.role === 'institute') {
        if (db) await updateDoc(doc(db, 'profiles', user.uid), { 'preferences.theme': next, updatedAt: serverTimestamp() });
        return;
      }

      if (role === 'employer') {
        await upsertEmployerProfile(user.uid, {
          preferences: {
            ...(profile?.preferences ?? {}),
            theme: next,
          },
        });
        return;
      }

      await upsertProfile(user.uid, {
        preferences: {
          ...(profile?.preferences ?? {}),
          theme: next,
        },
      });
    },
    [isMarketing, profile?.preferences, profile?.role, role, syncPreference, user?.uid],
  );

  const toggleLightDark = useCallback(async () => {
    await setThemePreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setThemePreference]);

  const value = useMemo(
    () => ({ preference, resolved, isMarketing, setThemePreference, toggleLightDark }),
    [preference, resolved, isMarketing, setThemePreference, toggleLightDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemePreference() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemePreference must be used within ThemeProvider');
  }
  return ctx;
}
